import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarDays, Check, Clock, MapPin, X, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import PunchLocationModal from '../../../shared/components/ui/PunchLocationModal';
import VisitsModal from './components/VisitsModal';
import { groupVisits, visitKey } from './components/visitGroups';
import DayMoves from './components/DayMoves';
import { movePoints } from './components/dayMoveLines';
import { formatAwayDistance, punchOutPlace } from './components/punchOutPlace';
import {
  getAttendanceRegister,
  getStaffVisits,
  getOfficeLocations,
  getPendingRegularizations,
  decideRegularization
} from '../../services/api/implementation/impl';
import {
  StatusPill,
  Tile,
  toneForStatus,
  Callout
} from '../../../shared/components/ui/primitives';

const TABS = [
  { key: 'register', label: 'Register' },
  { key: 'requests', label: 'Correction requests' }
];

/**
 * Employee attendance, read and corrected from the back office.
 *
 * The register shows what people recorded from the app; the requests tab is
 * where a manager decides corrections. Nothing here captures a location — the
 * portal has no device to read one from, and an approved correction is recorded
 * as coming from a person rather than dressed up as a punch.
 *
 * Visibility is the server's decision, not this screen's: it returns the
 * caller's own days plus their direct reports'. A manager sees their team, and
 * somebody with no reports sees only themselves.
 */
const AttendanceRegister = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=employees' });
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('register');
  const [from, setFrom] = useState(() => firstOfMonth());
  const [to, setTo] = useState(() => today());
  const [mapRow, setMapRow] = useState(null);

  const register = useQuery({
    queryKey: ['attendanceRegister', from, to],
    queryFn: () => getAttendanceRegister({ from, to }),
    enabled: tab === 'register'
  });

  // Customer visits made on site days, shown per row.
  const visitsQuery = useQuery({
    queryKey: ['staffVisits', from, to],
    queryFn: () => getStaffVisits({ from, to }),
    enabled: tab === 'register'
  });
  const visitsByDay = useMemo(() => groupVisits(visitsQuery.data?.data), [visitsQuery.data]);
  const [visitRow, setVisitRow] = useState(null);

  const offices = useQuery({
    queryKey: ['officeLocations'],
    queryFn: () => getOfficeLocations(),
    enabled: tab === 'register'
  });

  const requests = useQuery({
    queryKey: ['attendanceRegularizations'],
    queryFn: getPendingRegularizations
  });

  const decide = useMutation({
    mutationFn: ({ id, approve }) => decideRegularization(id, approve),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['attendanceRegularizations'] });
      queryClient.invalidateQueries({ queryKey: ['attendanceRegister'] });
      toast.success(variables.approve ? 'Correction approved' : 'Correction rejected');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not record the decision')
  });

  const rows = register.data?.data ?? [];
  const officeRows = offices.data?.data ?? [];
  const pending = requests.data?.data ?? [];

  const summary = useMemo(() => {
    const present = rows.filter((r) => r.status === 'PRESENT').length;
    const missingPunchOut = rows.filter((r) => r.check_in && !r.check_out).length;
    const flagged = rows.filter((r) => isOutOfRange(r)).length;
    return { present, missingPunchOut, flagged, total: rows.length };
  }, [rows]);

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Attendance' }} />

      <div className="p-4 space-y-4">
        <div className="flex gap-2 border-b border-gray-200">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === t.key
                  ? 'border-brand-700 text-brand-800'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
              {t.key === 'requests' && pending.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-warning-bg text-warning-fg text-xs">
                  {pending.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === 'register' ? (
          <>
            <div className="flex flex-wrap gap-3 items-end">
              <DateField label="From" value={from} onChange={setFrom} />
              <DateField label="To" value={to} onChange={setTo} />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Tile variant="card" label="Days recorded" value={summary.total}
            icon={<CalendarDays size={18} />}
          />
              <Tile variant="card" label="Present" value={summary.present}
            icon={<Check size={18} />}
          />
              <Tile variant="card"
                label="Missing punch out"
                value={summary.missingPunchOut}
                tone={summary.missingPunchOut > 0 ? 'warning' : undefined}
              />
              <Tile variant="card"
                label="Away from site"
                value={summary.flagged}
                tone={summary.flagged > 0 ? 'warning' : undefined}
              />
            </div>

            {register.isFetching ? (
              <Loader />
            ) : (
              <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <Th>Employee</Th>
                      <Th>Date</Th>
                      <Th>In</Th>
                      <Th>Out</Th>
                      <Th>Hours</Th>
                      <Th>Status</Th>
                      <Th>Location</Th>
                      <Th>Visits</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={8} className="text-center py-10 text-gray-500">
                          Nothing recorded in this range.
                        </td>
                      </tr>
                    )}
                    {rows.map((row) => (
                      <tr key={row.id} className="border-t border-gray-100">
                        <Td>
                          <div className="font-medium text-gray-900">
                            {row.user?.name ?? `#${row.user_id}`}
                          </div>
                          {row.project?.name && (
                            <div className="text-xs text-gray-500">
                              {row.project.name}
                            </div>
                          )}
                        </Td>
                        <Td>{formatDate(row.date)}</Td>
                        <Td>{formatTime(row.check_in)}</Td>
                        <Td>
                          {row.check_out ? (
                            formatTime(row.check_out)
                          ) : row.check_in ? (
                            <span className="text-warning-fg flex items-center gap-1">
                              <Clock size={12} /> missing
                            </span>
                          ) : (
                            '—'
                          )}
                        </Td>
                        <Td>{row.worked_hours ? Number(row.worked_hours) : '—'}</Td>
                        <Td>
                          <AttendancePill row={row} />
                        </Td>
                        <Td>
                          <LocationCell
                            row={row}
                            offices={officeRows}
                            onOpen={() => setMapRow(row)}
                          />
                          <DayMoves moves={row.moves} formatTime={formatTime} className="mt-1.5" />
                        </Td>
                        <Td>
                          {(() => {
                            const dayVisits = visitsByDay.get(visitKey(row.user_id, row.date)) || [];
                            if (!dayVisits.length) return <span className="text-gray-400">—</span>;
                            return (
                              <button
                                type="button"
                                onClick={() => setVisitRow({ row, visits: dayVisits })}
                                className="text-[#2A565E] font-medium hover:underline">
                                {dayVisits.length} visit{dayVisits.length === 1 ? '' : 's'}
                              </button>
                            );
                          })()}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <VisitsModal
              open={!!visitRow}
              onClose={() => setVisitRow(null)}
              title={`Visits · ${visitRow?.row?.user?.name ?? (visitRow ? `#${visitRow.row.user_id}` : '')}`}
              subtitle={visitRow ? formatDate(visitRow.row.date) : ''}
              visits={visitRow?.visits ?? []}
            />

            <PunchLocationModal
              open={!!mapRow}
              onClose={() => setMapRow(null)}
              title={mapRow?.user?.name ?? (mapRow ? `#${mapRow.user_id}` : '')}
              subtitle={joinMeta(mapRow?.project?.name, formatDate(mapRow?.date))}
              place={mapRow ? placeLabel(mapRow, officeRows) : null}
              eventLabel={
                mapRow?.check_in
                  ? `Punch in · ${formatTime(mapRow.check_in)}`
                  : 'Punch in'
              }
              accuracyM={mapRow?.punch_in_accuracy_m}
              lat={mapRow?.punch_in_lat}
              lng={mapRow?.punch_in_lng}
              points={mapRow ? punchPoints(mapRow) : []}
              fence={mapRow ? fenceFor(mapRow, officeRows) : null}
              overlay={mapRow?.project?.name}
              caption="This is the employee's own punch from the app."
            />
          </>
        ) : (
          <>
            <Callout tone="info" collapsible icon={<Info size={14} />} title="About corrections" className="sm:max-w-2xl">
              Requests from people who report to you. Approving applies the new times; the original punch stays visible.
            </Callout>

            {requests.isFetching ? (
              <Loader />
            ) : pending.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg py-10 text-center text-gray-500">
                No correction requests waiting.
              </div>
            ) : (
              <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <Th>Employee</Th>
                      <Th>Date</Th>
                      <Th>Recorded</Th>
                      <Th>Requested</Th>
                      <Th>Reason</Th>
                      <Th>Decision</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map((row) => (
                      <tr key={row.id} className="border-t border-gray-100">
                        <Td>
                          <div className="font-medium text-gray-900">
                            {row.user?.name ?? `#${row.user_id}`}
                          </div>
                        </Td>
                        <Td>{formatDate(row.date)}</Td>
                        <Td className="text-gray-500">
                          {row.check_in || row.check_out
                            ? `${formatTime(row.check_in)} → ${formatTime(row.check_out)}`
                            : 'Nothing recorded'}
                        </Td>
                        <Td className="font-medium text-gray-900">
                          {formatTime(row.requested_check_in)} →{' '}
                          {formatTime(row.requested_check_out)}
                        </Td>
                        <Td className="text-gray-600 text-xs max-w-xs">
                          {row.regularization_reason ?? '—'}
                        </Td>
                        <Td>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              disabled={decide.isPending}
                              onClick={() => decide.mutate({ id: row.id, approve: true })}
                              className="flex items-center gap-1 px-3 py-1.5 rounded bg-success text-white text-xs font-medium disabled:opacity-50"
                            >
                              <Check size={13} /> Approve
                            </button>
                            <button
                              type="button"
                              disabled={decide.isPending}
                              onClick={() =>
                                decide.mutate({ id: row.id, approve: false })
                              }
                              className="flex items-center gap-1 px-3 py-1.5 rounded border border-danger-border text-danger-fg text-xs font-medium disabled:opacity-50"
                            >
                              <X size={13} /> Reject
                            </button>
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

/**
 * A day's attendance. A pending correction outranks the marked status,
 * because that is the thing somebody has to act on.
 */
const AttendancePill = ({ row }) => {
  if (row.regularization_status === 'PENDING') {
    return (
      <StatusPill dense tone="warning">
        Correction pending
      </StatusPill>
    );
  }
  const LABELS = {
    PRESENT: 'Present',
    ABSENT: 'Absent',
    HALF_DAY: 'Half day',
    LEAVE: 'Leave',
    WEEK_OFF: 'Week off',
    HOLIDAY: 'Holiday'
  };
  return (
    <StatusPill dense tone={toneForStatus(row.status)}>
      {LABELS[row.status] ?? 'Not marked'}
    </StatusPill>
  );
};

/**
 * The coordinate is the employee's own punch from the app. Show the office or
 * site they were at — raw lat/lng is not something a person can read.
 */
const LocationCell = ({ row, offices, onOpen }) => {
  if (row.location_source === 'PORTAL') {
    return <span className="text-xs text-gray-500">Corrected manually</span>;
  }
  if (row.location_source === 'LEGACY_APP') {
    return <span className="text-xs text-gray-500">No location (old app)</span>;
  }
  if (row.punch_in_lat == null) {
    return <span className="text-xs text-gray-400">—</span>;
  }

  const outOfRange = isOutOfRange(row);
  const place = placeLabel(row, offices);

  return (
    <button type="button" onClick={onOpen} className="text-left text-xs max-w-[220px]">
      <div className="flex items-start gap-1 text-brand-700 hover:underline font-medium">
        <MapPin size={12} className="mt-0.5 shrink-0" />
        <span>{place}</span>
      </div>
      {row.punch_in_distance_m != null && (
        <div className={outOfRange ? 'text-warning-fg flex items-center gap-1' : 'text-gray-400'}>
          {outOfRange && <AlertTriangle size={11} />}
          {formatDistance(row.punch_in_distance_m)} from {place}
        </div>
      )}
      {row.location_source === 'GPS_COARSE' && (
        <div className="text-gray-400">approximate fix</div>
      )}
      <PunchOutLine row={row} fence={fenceFor(row, offices)} />
      <div className="text-gray-400">View map</div>
    </button>
  );
};

/**
 * Where the day ended. Flagged when an office day was punched out away from
 * the office, with the reason the employee gave.
 */
const PunchOutLine = ({ row, fence }) => {
  const place = punchOutPlace(row, fence);
  if (!place) return null;
  if (place.kind === 'site') return <div className="text-gray-400">Out at a site</div>;
  if (place.kind === 'wfh') return <div className="text-gray-400">Out · working from home</div>;
  if (place.kind === 'office') {
    return <div className="text-gray-400">Out at {place.name ?? 'the office'}</div>;
  }
  const far = formatAwayDistance(place.distance_m);
  return (
    <>
      <div className="text-warning-fg flex items-start gap-1">
        <AlertTriangle size={11} className="mt-0.5 shrink-0" />
        <span>
          Punched out {far ? `${far} ` : ''}away{place.name ? ` from ${place.name}` : ''}
        </span>
      </div>
      {row.punch_out_note && (
        <div className="text-gray-500 italic">“{row.punch_out_note}”</div>
      )}
    </>
  );
};

const DateField = ({ label, value, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
    />
  </div>
);

/** Matches the server's default radius; a row without a distance is never flagged. */
function isOutOfRange(row) {
  return row.punch_in_distance_m != null && row.punch_in_distance_m > 500;
}

function placeLabel(row, offices = []) {
  if (row.punch_mode === 'WFH') return 'Working from home';
  if (row.officeLocation?.name) return row.officeLocation.name;
  const match = matchedOffice(row, offices);
  if (match?.name) return match.name;
  if (row.project?.site_lat != null && nearProjectSite(row)) {
    return row.project.name;
  }
  return row.project?.name || 'Recorded from app';
}

function matchedOffice(row, offices = []) {
  if (row.officeLocation) return row.officeLocation;
  return offices.find((office) => {
    const radius = Number(office.radius_m) || 150;
    return (
      metresBetween(row.punch_in_lat, row.punch_in_lng, office.lat, office.lng) <=
      radius
    );
  });
}

function nearProjectSite(row) {
  if (row.project?.site_lat == null) return false;
  const radius = Number(row.project.geofence_radius_m) || 350;
  return (
    metresBetween(
      row.punch_in_lat,
      row.punch_in_lng,
      row.project.site_lat,
      row.project.site_lng
    ) <= radius
  );
}

function punchPoints(row) {
  return [
    {
      lat: row.punch_in_lat,
      lng: row.punch_in_lng,
      accuracy_m: row.punch_in_accuracy_m,
      distance_m: row.punch_in_distance_m,
      label: 'Punch in',
      time: formatTime(row.check_in),
      kind: 'IN'
    },
    ...movePoints(row.moves, formatTime),
    ...(row.punch_out_lat
      ? [
          {
            lat: row.punch_out_lat,
            lng: row.punch_out_lng,
            accuracy_m: row.punch_out_accuracy_m,
            label: 'Punch out',
            time: formatTime(row.check_out),
            kind: 'OUT'
          }
        ]
      : [])
  ];
}

function joinMeta(...parts) {
  return parts.filter(Boolean).join(' · ') || null;
}

function fenceFor(row, offices = []) {
  const office = matchedOffice(row, offices);
  if (office?.lat != null) {
    return {
      lat: office.lat,
      lng: office.lng,
      radius_m: office.radius_m,
      name: office.name
    };
  }
  if (row.project?.site_lat != null) {
    return {
      lat: row.project.site_lat,
      lng: row.project.site_lng,
      radius_m: row.project.geofence_radius_m,
      name: row.project.name
    };
  }
  return null;
}

function metresBetween(lat1, lng1, lat2, lng2) {
  const a = Number(lat1);
  const b = Number(lng1);
  const c = Number(lat2);
  const d = Number(lng2);
  if (![a, b, c, d].every(Number.isFinite)) return Number.POSITIVE_INFINITY;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(c - a);
  const dLng = toRad(d - b);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a)) * Math.cos(toRad(c)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(h)));
}

function formatDistance(metres) {
  if (metres < 1000) return `${metres}m`;
  return `${(metres / 1000).toFixed(1)}km`;
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: '2-digit'
  });
}

function formatTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

function firstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}-01`;
}

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}-${`${d.getDate()}`.padStart(2, '0')}`;
}

const Th = ({ children }) => (
  <th className="text-left font-medium px-4 py-3 whitespace-nowrap">{children}</th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
);

export default AttendanceRegister;
